/**
 * Network entity class
 */

import { PLATFORM_TYPES } from 'app/core/constants'
import { clone, has } from 'app/helpers/object'

/**
 * NetworkFactory
 * returns Network entity class
 *
 * @class NetworkFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Network} class
 *
 * @ngdoc factory
 * @name mwApp.factory:NetworkFactory
 */
/* @ngInject */
export default function NetworkFactory ($http, $injector, alertLiteSvc, Entity, Type, FileUploadMixin, InappSettings, authSvc) {
  /* === Network class === */

  /**
   * Network model
   *
   * @class Network
   * @classdesc Network model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @return {models.Network} Network entity
   */
  function Network () {
    const obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Network.prototype = Object.create(Entity.prototype)
  Network.prototype.constructor = Network

  /* === Network instance methods & fields === */

  Object.assign(Network.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Network#
     * @type {Object}
     */
    _fields: {
      $startChannelSettingsDto: [{
        channel: {
          id: Number,
          name: String,
          enabled: Boolean,
          inSubscription: Boolean
        },
        subscription: {
          id: Number,
          name: String,
          enabled: Boolean
        }
      }],
      id: Number,
      name: String,
      backgroundColor: String,
      categorySets: ['CategorySet'],
      customVideoUrl: String,
      deviceCount: 0,
      hasAssignedAcl: Boolean,
      hasAvodSubscription: Boolean,
      listingType: Type(String('Sequence')),
      multiorgEnabled: false,
      multiorgId: Number,
      networkCatchupLinks: ['NetworkCatchupLink'],
      networkChannelLinks: ['NetworkChannelLink'],
      networkThemeLinks: ['NetworkThemeLink'],
      pincode: String,
      platforms: Type.CSV(';'),
      prefix: String,
      startChannelSettingsEnabled: Boolean,
      startChannelSettingsDto: [{
        subscriptionId: Number,
        channelId: Number
      }],
      startPageType: String, // TV,VOD,Radio,Catchup or null
      staticChannel: 'Channel',
      screenSaverSettings: {
        value: Number,
        enabled: Boolean,
        useSystemDefault: Boolean
      },
      subscriberCount: Number,
      subscribers: ['Subscriber'],
      timezone: String,
      voucherSubscribersAllowed: false,
      logoUrl: String,
      apiAccessUser: {
        enabled: Boolean,
        login: String,
        token: String
      },
      allowedExclusivePaymentSystems: [{ paymentSystemId: Number, paymentSystemType: String }],
      $ecwidEnabled: false,
      $ecwidSettings: 'Ecwid'
    },

    /**
     * url template
     *
     * @memberof models.Network#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Network#
     * @type {String}
     */
    _publicUrlTemplate: 'organizations/{{::id}}',

    /**
     * Clone network
     *
     * @memberof models.Network#
     * @return {Promise} promise
     */
    _duplicate: function () {
      return $http.patch(this._url + '/copy', '"COPY"')
        .then(
          response => {
            var message = `Organization cloned into &quot;${this.name} copy&quot;<br>`
            if (response.data && response.data.id) {
              message += `<a ui-sref="app.organizations.organization.edit({ packageId: ${response.data.id} })" ` +
                            'class="btn-buttons_link-button_const-white" ' +
                            'ng-click="closeAlert()">Open It</a> &nbsp; <span>|</span> &nbsp; '
            }
            message += '<a ui-sref="app.organizations.list" class="btn-buttons_link-button_const-white" ng-click="closeAlert()">Open List</a>'
            alertLiteSvc.alert(message, 'success', 10000)
            return response
          }
        ).catch(alertLiteSvc.alertError)
    },

    /**
     * Get platforms array with checked flags, depending on network platforms array
     *
     * @memberof models.Network#
     * @param {String[]} def - default flags
     * @return {Object[]}
     */
    _enhancePlatforms: function (def) {
      const ret = clone(PLATFORM_TYPES.filter((p) => {
        return !(!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED') && p.name === 'WEB')
      }))
      if (this._isNew() && def) {
        for (let i = ret.length; i--;) {
          if (def.indexOf(ret[i].name) !== -1) {
            ret[i].checked = true
          }
        }
      } else
      if (this.platforms && this.platforms.length) {
        for (let i = ret.length; i--;) {
          ret[i].checked = this.platforms.indexOf(ret[i].name) !== -1
        }
      }
      return ret
    },

    /**
     * Set network platforms array from platforms array with checked flags
     *
     * @memberof models.Network#
     * @param {Object[]} platforms - platforms array
     */
    _reducePlatforms: function (platforms) {
      this.platforms = []
      for (var i = platforms.length; i--;) {
        if (platforms[i].checked) {
          this.platforms.push(platforms[i].name)
        }
      }
      return this
    },

    /**
     * Get network info
     *
     * @memberof models.Network#
     */
    _loadInfo: function () {
      if (this._loadingInfo || this._info) {
        return
      }

      this._loadingInfo = true
      this._info = null

      // load info from Nora backend
      Network.loadRaw(this._url + '/info', {
        activeSubscribers: 0,
        expiredSubscribers: 0,
        activeDevices: 0,
        inActiveDevices: 0
      }).then(
        info => {
          this._loadingInfo = false
          this._info = info
        }
      ).catch(alertLiteSvc.alertError)

      // load health check and catchup info
      // TODO
    },

    /**
     * Get devices settings
     *
     * @memberof models.Network#
     */
    _getDevicesSettings: function () {
      if (!this._url) {
        return this._updateUrl()
          .then(() => this._getDevicesSettings())
      }

      return $http.get(this._url + '/settings/devices')
        .then((response) => response.data)
        .catch(alertLiteSvc.alertError)
    },

    /**
     * Save devices settings
     *
     * @param {Object} settings - params to request
     * @returns {Promise}
     * @private
     */
    _saveDevicesSettings: function (settings) {
      return $http.put(this._url + '/settings/devices', settings)
    },

    /**
     * Upload file for network
     *
     * @memberof models.Network#
     */
    _uploadFileNetwork: function (file, params) {
      if (!this._url) {
        return this._updateUrl()
          .then(() => this._uploadFileNetwork(file, params))
      }

      return this._uploadFile(this._url + '/upload', file, params)
    },

    /**
     * Get image URL for given size
     *
     * @memberof models.Network#
     * @param  {String} size - e.g. '530x720', '235x332', '137x193'
     * @return {String}
     */
    _imageUrlFor: function (size) {
      return this.logoUrl
        ? this.logoUrl.replace('{{size}}', size)
        : ''
    },
    /**
     *
     * @returns {startChannelSettingsDto}
     * @private
     */
    _loadStartChannels: function () {
      return $http.get(this._url + '/start-channels')
        .data()
        .then(data => data.map(i => ({
          subscription: {
            id: i.subscriptionId,
            name: i.subscriptionName,
            enabled: i.subscriptionEnabled
          },
          channel: {
            id: i.channelId,
            name: i.channelName,
            enabled: i.channelEnabled,
            inSubscription: i.channelInSubscription
          }
        })))
        .to(this.$startChannelSettingsDto = [])
        .catch(alertLiteSvc.alertError)
    }

  }, FileUploadMixin)

  Network.loadAllowedCurrencies = function (networkId) {
    return $http.get(`/nora/api/networks/${networkId}/subscriptions/0/extra`)
  }

  Network.getApiAccess = function (networkId) {
    return $http.get(`/nora/api/extusers/${networkId}/api-access`)
  }

  Network.getLoginOptions = function (networkId) {
    return $http.get(`/nora/api/networks/${networkId}/settings/login-options`).then(response => {
      return has('payload')(response.data)
        ? new InappSettings(response.data.payload)
        : new InappSettings(response.data)
    })
  }

  Network.getLoginOptionsSubscriptions = function (networkId) {
    return $http.get(`/nora/api/networks/${networkId}/settings/login-options/extra/subscriptions`).then(response => {
      const data = has('payload')(response.data)
        ? response.data.payload
        : response.data
      return data.subscriptions
    })
  }

  /* === Network static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(Network, Entity)
  Network._name = 'Network'
  Network._publicName = 'Organization'
  Network.init()

  // permissions
  Network.access = {
    view: 'viewNetwork createChannel | editChannel | createDevice | editDevice | createCategory | ' +
          'editCategory | createUser | editUser | createSubscriber | viewVod | createVod | editVod |',
    create: 'createNetwork',
    edit: 'editNetwork',
    delete: 'deleteNetwork'
  }

  // extra
  Network.extra = {
    catchups: ['Catchup'],
    defaultNetworkScreenSaver: Number,
    defaultNetworkScreenSaverEnabled: Boolean,
    radioChannels: ['Channel'],
    systemTimezone: String,
    themes: ['Theme'],
    tvChannels: ['Channel']
  }

  return Network
}
